import React, { useRef, useState, useEffect } from 'react';
import './RichTextEditor.css';

interface RichTextEditorProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  rows?: number;
}

const RichTextEditor: React.FC<RichTextEditorProps> = ({ value, onChange, placeholder, rows = 3 }) => {
  const editorRef = useRef<HTMLDivElement>(null);
  const [isFocused, setIsFocused] = useState(false);

  useEffect(() => {
    if (editorRef.current) {
      const currentContent = editorRef.current.innerHTML;
      const newContent = value || '';
      // Обновляем только если содержимое действительно изменилось
      if (currentContent !== newContent) {
        editorRef.current.innerHTML = newContent;
      }
    }
  }, [value]);

  const handleInput = () => {
    if (editorRef.current) {
      // Всегда отправляем HTML содержимое, даже если оно пустое
      // Это позволяет сохранять переносы строк даже в пустом редакторе
      onChange(editorRef.current.innerHTML);
    }
  };

  const handlePaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const text = e.clipboardData.getData('text/plain');
    document.execCommand('insertText', false, text);
  };

  const applyFormat = (command: string, value?: string) => {
    document.execCommand(command, false, value);
    editorRef.current?.focus();
    handleInput();
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    // Обрабатываем Enter для создания переноса строки
    if (e.key === 'Enter') {
      e.preventDefault();
      // И для Enter, и для Shift+Enter вставляем <br> для переноса строки
      // Это гарантирует, что перенос строки всегда работает, даже если нет текста
      if (editorRef.current) {
        const selection = window.getSelection();
        if (selection && selection.rangeCount > 0) {
          const range = selection.getRangeAt(0);
          
          // Если редактор пустой или курсор в начале, просто добавляем <br>
          if (!editorRef.current.textContent || editorRef.current.textContent.trim() === '') {
            editorRef.current.innerHTML = '<br>';
            // Устанавливаем курсор после <br>
            const newRange = document.createRange();
            newRange.setStart(editorRef.current, 0);
            newRange.setEnd(editorRef.current, 0);
            selection.removeAllRanges();
            selection.addRange(newRange);
          } else {
            // Если есть текст, вставляем <br> в позицию курсора
            range.deleteContents();
            const br = document.createElement('br');
            range.insertNode(br);
            // Устанавливаем курсор после <br>
            range.setStartAfter(br);
            range.collapse(true);
            selection.removeAllRanges();
            selection.addRange(range);
          }
        } else if (editorRef.current) {
          // Fallback: если нет selection, просто добавляем <br>
          if (!editorRef.current.textContent || editorRef.current.textContent.trim() === '') {
            editorRef.current.innerHTML = '<br>';
          } else {
            document.execCommand('insertHTML', false, '<br>');
          }
        }
        handleInput();
      }
    }
  };

  return (
    <div className="rich-text-editor">
      <div className="editor-toolbar">
        <button
          type="button"
          className="toolbar-btn"
          onClick={() => applyFormat('bold')}
          title="Жирный (Ctrl+B)"
        >
          <strong>B</strong>
        </button>
        <button
          type="button"
          className="toolbar-btn"
          onClick={() => applyFormat('italic')}
          title="Курсив (Ctrl+I)"
        >
          <em>I</em>
        </button>
        <button
          type="button"
          className="toolbar-btn"
          onClick={() => applyFormat('underline')}
          title="Подчеркнутый (Ctrl+U)"
        >
          <u>U</u>
        </button>
      </div>
      <div
        ref={editorRef}
        contentEditable
        className={`editor-content ${isFocused ? 'focused' : ''}`}
        onInput={handleInput}
        onPaste={handlePaste}
        onKeyDown={handleKeyDown}
        onFocus={() => setIsFocused(true)}
        onBlur={() => setIsFocused(false)}
        data-placeholder={placeholder || 'Добавить комментарий...'}
        style={{ minHeight: `${rows * 1.5}em` }}
      />
    </div>
  );
};

export default RichTextEditor;
